import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

import { ThunkActionDispatch } from "@typings/reduxThunkActions";

import { TrashRounded } from "@ui/Assets/Symbolicons/TrashRounded";

import { stringifyFalsies } from "@services/StringifyFalsies";
import { AsyncActionResult } from "@services/AsyncActionCreatorFactory";

import { showToast } from "@containers/Toastify";

import { useTranslate } from "@hooks/useTranslate";

import { OPTIONS_MODALS } from "@app/pages/ClientDashboard/OptionsDashboard.modals";
import { Modal } from "@app/containers/Modal";
import {
  deleteHoliday,
  clearProcessedHoliday,
} from "@app/redux/holidays/holidays.actions";
import { getProcessedHoliday } from "@app/redux/holidays/holidays.selectors";
import { hideLoading, showLoading } from "@app/redux/loading/loading.actions";
import { useModalStore } from "@app/hooks/useModalStore";

const ConfirmHolidayDeletion = (): React.ReactElement | null => {
  const dispatch = useDispatch<ThunkActionDispatch>();
  const translate = useTranslate();
  const { hideModal } = useModalStore();

  const processedHoliday = useSelector(getProcessedHoliday);
  const holidayId = processedHoliday?.get("id");
  const startDate = stringifyFalsies(processedHoliday?.get("startDate"));
  const endDate = stringifyFalsies(processedHoliday?.get("endDate"));

  const showContent = holidayId && startDate && endDate;

  if (!showContent) {
    return null;
  }

  return (
    <Modal
      title={translate("batmaid_pwa_client_options.confirm_holiday_deletion")}
      id={OPTIONS_MODALS.CONFIRM_HOLIDAY_DELETION}
      submit={async () => {
        dispatch(showLoading());

        const { onSuccess, onError } = await dispatch<
          Promise<AsyncActionResult>
        >(deleteHoliday());

        onSuccess(() => {
          showToast(
            translate("batmaid_pwa_holidays.holiday_deletion_success"),
            {
              type: "success",
            },
          );
        });

        onError(err => {
          showToast(
            translate("batmaid_pwa_holidays.holiday_deletion_error") ||
              err.message,
            {
              type: "error",
            },
          );
        });

        dispatch(hideLoading());
        hideModal(OPTIONS_MODALS.CONFIRM_HOLIDAY_DELETION);
        dispatch(clearProcessedHoliday());
      }}
      submitBtnColor="blue"
    >
      <IconContainer>
        <TrashRounded variant="line" color="grey" size={50} height={50} />
      </IconContainer>
    </Modal>
  );
};

export { ConfirmHolidayDeletion };

ConfirmHolidayDeletion.displayName = "ConfirmHolidayDeletion";

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: ${props => props.theme.margins.base_x4} 0;
`;
